// The host end of the sealed channel. Wraps a plugin worker's RpcChannel in an
// RpcPeer, answers the plugin's `slate.*` calls by delegating to host services
// (the command registry, host-rendered UI, the sidebar), and exposes the
// host-to-worker direction (invoke a command, fetch tree children). Framework-
// agnostic and fully unit-testable: it depends only on a services object.

import {
  RpcPeer,
  HOST_METHODS,
  WORKER_METHODS,
  EVENTS,
  type RpcChannel,
  type QuickPickItem,
  type MessageOptions,
  type TreeItem,
  type FoldRange,
  type PermissionCapability,
  type PermissionState,
} from '@slate/plugin-api';

/** What the host plugs in: every effect a plugin can request lands here. */
export interface PluginHostServices {
  onCommandRegistered(pluginId: string, commandId: string): void;
  onCommandUnregistered(pluginId: string, commandId: string): void;
  onViewRegistered(pluginId: string, viewId: string): void;
  onViewUnregistered(pluginId: string, viewId: string): void;
  onSurfaceRegistered(pluginId: string, surfaceId: string): void;
  onSurfaceUnregistered(pluginId: string, surfaceId: string): void;
  onSurfaceViewRegistered(pluginId: string, viewId: string): void;
  onSurfaceViewUnregistered(pluginId: string, viewId: string): void;
  /** A worker pushed a message OUT to a surface's iframe(s). */
  onSurfaceMessage(pluginId: string, surfaceId: string, message: unknown): void;
  /** A plugin asked to open a modal surface; resolves when the user closes it. */
  showModal(pluginId: string, surfaceId: string): Promise<void>;
  showMessage(message: string, options?: MessageOptions): Promise<string | undefined>;
  showQuickPick(
    items: QuickPickItem[],
    options?: { placeholder?: string },
  ): Promise<QuickPickItem | undefined>;
  executeCommand(id: string, args: unknown[]): Promise<unknown>;
  permissionQuery(
    pluginId: string,
    capability: PermissionCapability,
  ): PermissionState | Promise<PermissionState>;
  permissionRequest(pluginId: string, capability: PermissionCapability): Promise<boolean>;
  fsRead(pluginId: string, path: string): Promise<string>;
  /** The active editor's text (the in-app document), or undefined when none is
   *  open. Not permission-gated: it is content the user is already viewing.
   *  Optional: a host that omits it makes `slate.editor.getActiveText()` resolve
   *  to undefined. */
  getActiveText?(): string | undefined | Promise<string | undefined>;
  /** A plugin registered a fold provider (slate.editor.registerFoldProvider).
   *  Optional, so existing service literals need no change. */
  onFoldProviderRegistered?(
    pluginId: string,
    providerId: string,
    extensions: readonly string[],
  ): void;
  /** A plugin's fold provider was unregistered (disposable disposed). Optional. */
  onFoldProviderUnregistered?(pluginId: string, providerId: string): void;
}

export class PluginConnection {
  private readonly peer: RpcPeer;

  constructor(
    channel: RpcChannel,
    readonly pluginId: string,
    services: PluginHostServices,
  ) {
    this.peer = new RpcPeer(channel);

    this.peer.handle(HOST_METHODS.commandsRegister, (p: unknown) => {
      services.onCommandRegistered(pluginId, (p as { id: string }).id);
    });
    this.peer.handle(HOST_METHODS.commandsUnregister, (p: unknown) => {
      services.onCommandUnregistered(pluginId, (p as { id: string }).id);
    });
    this.peer.handle(HOST_METHODS.commandsExecute, (p: unknown) => {
      const { id, args } = p as { id: string; args?: unknown[] };
      return services.executeCommand(id, args ?? []);
    });
    this.peer.handle(HOST_METHODS.windowShowMessage, (p: unknown) => {
      const { message, options } = p as { message: string; options?: MessageOptions };
      return services.showMessage(message, options);
    });
    this.peer.handle(HOST_METHODS.windowShowQuickPick, (p: unknown) => {
      const { items, options } = p as {
        items: QuickPickItem[];
        options?: { placeholder?: string };
      };
      return services.showQuickPick(items, options);
    });
    this.peer.handle(HOST_METHODS.viewsRegister, (p: unknown) => {
      services.onViewRegistered(pluginId, (p as { viewId: string }).viewId);
    });
    this.peer.handle(HOST_METHODS.viewsUnregister, (p: unknown) => {
      services.onViewUnregistered(pluginId, (p as { viewId: string }).viewId);
    });
    this.peer.handle(HOST_METHODS.surfaceRegister, (p: unknown) => {
      services.onSurfaceRegistered(pluginId, (p as { surfaceId: string }).surfaceId);
    });
    this.peer.handle(HOST_METHODS.surfaceUnregister, (p: unknown) => {
      services.onSurfaceUnregistered(pluginId, (p as { surfaceId: string }).surfaceId);
    });
    this.peer.handle(HOST_METHODS.viewsRegisterSurface, (p: unknown) => {
      services.onSurfaceViewRegistered(pluginId, (p as { viewId: string }).viewId);
    });
    this.peer.handle(HOST_METHODS.viewsUnregisterSurface, (p: unknown) => {
      services.onSurfaceViewUnregistered(pluginId, (p as { viewId: string }).viewId);
    });
    this.peer.handle(HOST_METHODS.surfacePost, (p: unknown) => {
      const { surfaceId, message } = p as { surfaceId: string; message: unknown };
      services.onSurfaceMessage(pluginId, surfaceId, message);
    });
    this.peer.handle(HOST_METHODS.windowShowModal, (p: unknown) => {
      return services.showModal(pluginId, (p as { surfaceId: string }).surfaceId);
    });
    this.peer.handle(HOST_METHODS.permissionsQuery, (p: unknown) => {
      return services.permissionQuery(pluginId, (p as { capability: PermissionCapability }).capability);
    });
    this.peer.handle(HOST_METHODS.permissionsRequest, (p: unknown) => {
      return services.permissionRequest(pluginId, (p as { capability: PermissionCapability }).capability);
    });
    this.peer.handle(HOST_METHODS.fsRead, (p: unknown) => {
      return services.fsRead(pluginId, (p as { path: string }).path);
    });
    this.peer.handle(HOST_METHODS.editorGetActiveText, () => {
      return services.getActiveText?.();
    });
    this.peer.handle(HOST_METHODS.editorRegisterFoldProvider, (p: unknown) => {
      const { providerId, extensions } = p as { providerId: string; extensions: readonly string[] };
      services.onFoldProviderRegistered?.(pluginId, providerId, extensions);
    });
    this.peer.handle(HOST_METHODS.editorUnregisterFoldProvider, (p: unknown) => {
      services.onFoldProviderUnregistered?.(pluginId, (p as { providerId: string }).providerId);
    });
  }

  /** Push a permission grant/revocation into the running plugin so it reacts live. */
  emitPermissionChanged(capability: PermissionCapability, state: PermissionState): void {
    this.peer.emit(EVENTS.permissionsChanged, { capability, state });
  }

  /** Drive the plugin's `activate` over the channel (works for both tiers). */
  activatePlugin(): Promise<void> {
    return this.peer.call(WORKER_METHODS.lifecycleActivate).then(() => undefined);
  }

  /** Drive the plugin's `deactivate`; ignore a teardown-race rejection. */
  deactivatePlugin(): Promise<void> {
    return this.peer.call(WORKER_METHODS.lifecycleDeactivate).then(
      () => undefined,
      () => undefined,
    );
  }

  /** Run one of the plugin's registered commands inside the sealed room. */
  invokeCommand(id: string, args: unknown[] = []): Promise<unknown> {
    return this.peer.call(WORKER_METHODS.commandInvoke, { id, args });
  }

  /** Deliver a message an iframe sent up into the owning plugin's worker. */
  deliverSurfaceMessage(surfaceId: string, message: unknown): Promise<void> {
    return this.peer
      .call(WORKER_METHODS.surfaceDeliver, { surfaceId, message })
      .then(() => undefined);
  }

  /** Fetch a plugin tree view's children for host rendering. */
  async getViewChildren(viewId: string, parentId?: string): Promise<TreeItem[]> {
    const out = await this.peer.call(WORKER_METHODS.viewGetChildren, { viewId, parentId });
    return (out as TreeItem[]) ?? [];
  }

  /** Pull fold ranges from one of the plugin's registered fold providers. */
  async getFolds(providerId: string, source: string, fileName: string): Promise<FoldRange[]> {
    const out = await this.peer.call(WORKER_METHODS.editorGetFolds, { providerId, source, fileName });
    return (out as FoldRange[]) ?? [];
  }

  dispose(): void {
    this.peer.dispose();
  }
}
